import { useAppSelector } from 'store';
import { Language } from '@net0-calc/common/types';
import { CalculatorFormValues, Co2FootprintsVOs } from 'common/types';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Co2FootprintVO } from 'vo';
import { ListItem } from './components';
import { selectors } from 'store/ducks/dictionary';
import { COLORS } from 'assets/colors';

const useTodayLocalDateString = () => {
  const localesMapping: Record<Language, string> = {
    [Language.ENGLISH]: 'en-US',
    [Language.DEUTSCH]: 'de-DE',
    [Language.FRANÇAIS]: 'fr-FR',
    [Language.ITALIANO]: 'it-IT',
  };

  const currentLanguage = useAppSelector(selectors.selectLanguage);
  const currentLocale = localesMapping[currentLanguage];
  const todayLocaleDateString = new Date().toLocaleDateString(currentLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return todayLocaleDateString;
};

const translationKeyPrefix = 'events.socialImageLayout';

type Props = {
  rootRef: React.RefObject<HTMLHtmlElement>;
  co2FootprintSumVO: Co2FootprintVO;
  co2FootprintsVOs: Co2FootprintsVOs;
  formValues: CalculatorFormValues;
};

const SocialImageLayout: FC<Props> = ({ rootRef, co2FootprintSumVO, co2FootprintsVOs, formValues }) => {
  const { t } = useTranslation();

  const todayLocaleDateString = useTodayLocalDateString();
  return (
    <html ref={rootRef}>
      <head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <style>
          {`
            html,
            body,
            div,
            span,
            applet,
            object,
            iframe,
            h1,
            h2,
            h3,
            h4,
            h5,
            h6,
            p,
            blockquote,
            pre,
            a,
            abbr,
            acronym,
            address,
            big,
            cite,
            code,
            del,
            dfn,
            em,
            img,
            ins,
            kbd,
            q,
            s,
            samp,
            small,
            strike,
            strong,
            sub,
            sup,
            tt,
            var,
            b,
            u,
            i,
            center,
            dl,
            dt,
            dd,
            ol,
            ul,
            li,
            fieldset,
            form,
            label,
            legend,
            table,
            caption,
            tbody,
            tfoot,
            thead,
            tr,
            th,
            td,
            article,
            aside,
            canvas,
            details,
            embed,
            figure,
            figcaption,
            footer,
            header,
            hgroup,
            menu,
            nav,
            output,
            ruby,
            section,
            summary,
            time,
            mark,
            audio,
            video {
              margin: 0;
              padding: 0;
              border: 0;
              font-size: 100%;
              font: inherit;
              vertical-align: baseline;
            }

            `}
        </style>
      </head>
      <body
        style={{
          width: '1000px',
          fontFamily: 'Poppins',
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '20px',
        }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            border: `16px solid ${COLORS.color2}`,
            background: COLORS.color5,
            padding: '80px',
          }}>
          <h1
            style={{
              fontWeight: 800,
              fontSize: '60px',
              lineHeight: '90px',
              color: COLORS.color6,
              marginBottom: '106px',
            }}>
            <span
              style={{ display: 'inline' }}
              dangerouslySetInnerHTML={{
                __html: t(`${translationKeyPrefix}.title`, { eventName: formValues.eventName }),
              }}></span>
            <span style={{ color: COLORS.color2, display: 'inline' }}> {co2FootprintSumVO.stringifiedValue}</span>
          </h1>
          <h2
            style={{
              fontWeight: 600,
              fontSize: '32px',
              lineHeight: '48px',
              color: COLORS.color6,
              marginBottom: '32px',
            }}>
            {t(`${translationKeyPrefix}.subtitle`)}
          </h2>
          <ul>
            <ListItem
              title={t(`${translationKeyPrefix}.co2FootprintList.travelAndAccommodation`)}
              value={co2FootprintsVOs.travelAndAccommodation.stringifiedValue}
              t={t}
              color={COLORS.chartColor1}
            />
            <ListItem
              title={t(`${translationKeyPrefix}.co2FootprintList.hospitality`)}
              value={co2FootprintsVOs.hospitality.stringifiedValue}
              t={t}
              color={COLORS.chartColor2}
            />
            <ListItem
              title={t(`${translationKeyPrefix}.co2FootprintList.catering`)}
              value={co2FootprintsVOs.catering.stringifiedValue}
              t={t}
              color={COLORS.chartColor3}
            />
            <ListItem
              title={t(`${translationKeyPrefix}.co2FootprintList.merchandise`)}
              value={co2FootprintsVOs.merchandise.stringifiedValue}
              t={t}
              color={COLORS.chartColor4}
            />
            <ListItem
              title={t(`${translationKeyPrefix}.co2FootprintList.durationAndVenueInfo`)}
              value={co2FootprintsVOs.eventDurationAndInfo.stringifiedValue}
              t={t}
              color={COLORS.chartColor5}
            />
          </ul>
          <table style={{ display: 'block', marginTop: 'auto' }}>
            <tr>
              <td style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'inline-block',
                    fontWeight: 500,
                    fontSize: '20px',
                    lineHeight: '30px',
                    color: '#333',
                  }}>
                  <span style={{ color: '#797979' }}>{t(`${translationKeyPrefix}.footer.dateTitle`)}</span>{' '}
                  {todayLocaleDateString}
                </div>
              </td>
              <td>
                <svg
                  viewBox="0 0 854 380"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ display: 'inline-block', width: '200px', height: '82px', float: 'right', color: 'black' }}>
                  <path
                    d="M284.55 212.051C272.08 199.711 256.62 193.071 238.28 192.471C237.36 192.441 236.43 192.421 235.49 192.421C233.91 192.421 232.35 192.461 230.81 192.551C226.74 192.771 222.82 193.291 219.04 194.111C213.92 195.221 209.06 196.881 204.48 199.091C197.9 202.261 191.88 206.581 186.43 212.051C173.34 225.201 166.88 241.501 166.88 261.131C166.88 280.761 173.34 297.061 186.43 310.211C199.53 323.371 215.93 329.851 235.49 329.851C255.05 329.851 271.46 323.361 284.55 310.211C297.65 297.061 304.11 280.761 304.11 261.131C304.11 241.501 297.65 225.201 284.55 212.051ZM270.57 297.251C261.32 306.551 249.8 311.281 235.66 311.281C221.52 311.281 209.83 306.551 200.75 297.251C191.67 287.961 187.13 275.861 187.13 261.141C187.13 250.821 189.36 241.801 193.83 234.121C195.45 231.311 197.39 228.671 199.62 226.231C199.99 225.821 200.37 225.421 200.75 225.031C207.75 217.991 216.04 213.571 225.79 211.841C228.93 211.281 232.22 211.001 235.66 211.001H236.11C250.05 211.101 261.59 215.841 270.57 225.031C279.64 234.321 284.18 246.421 284.18 261.141C284.18 275.861 279.65 287.951 270.57 297.251Z"
                    fill="currentColor"
                  />
                  <path
                    d="M701.93 201.88V225.72C691.11 215.91 679.24 211 666.14 211C651.83 211 640.3 215.73 631.39 225.19C622.48 234.65 617.96 246.75 617.96 261.47C617.96 276.19 622.5 287.41 631.57 296.87C640.65 306.33 652.17 311.06 666.14 311.06C679.23 311.06 691.1 305.98 701.75 295.81V319.83C690.75 326.5 678.53 329.82 665.44 329.82C647.11 329.82 631.57 323.68 618.65 311.59C604.51 298.27 597.53 281.61 597.53 261.46C597.53 240.25 604.52 222.9 618.65 209.58C630.87 198.01 646.76 192.23 666.3 192.23C679.91 192.23 691.61 195.39 701.56 201.69L701.91 201.87H701.93V201.88Z"
                    fill="currentColor"
                  />
                  <path
                    d="M735.96 212.051C749.06 198.901 765.46 192.421 785.02 192.421C804.58 192.421 820.98 199.091 834.08 212.051C847.17 225.201 853.63 241.501 853.63 261.131C853.63 280.761 847.17 297.061 834.08 310.211C820.98 323.361 804.58 329.851 785.02 329.851C765.46 329.851 749.06 323.371 735.96 310.211C722.87 297.061 716.41 280.761 716.41 261.131C716.41 241.501 722.87 225.201 735.96 212.051ZM750.27 297.251C759.35 306.551 771.04 311.281 785.18 311.281C799.32 311.281 810.84 306.551 820.09 297.251C829.17 287.951 833.7 275.861 833.7 261.141C833.7 246.421 829.16 234.321 820.09 225.031C811.01 215.741 799.32 211.001 785.18 211.001C771.04 211.001 759.52 215.731 750.27 225.031C741.19 234.331 736.66 246.421 736.66 261.141C736.66 275.861 741.2 287.961 750.27 297.251Z"
                    fill="currentColor"
                  />
                  <path
                    d="M526.06 184.49L463.93 327.62L484.86 327.68L526.07 231.37L569.97 327.92L588.87 327.98L526.06 184.48V184.49Z"
                    fill="currentColor"
                  />
                  <path
                    d="M134.9 82.4004L72.2403 223.06L10.1104 82.4004H31.0704L72.2504 177.38L116.02 82.4004H134.9Z"
                    fill="currentColor"
                  />
                  <path
                    d="M346.6 327.93H326.35V184.36L422.18 285.16V192.42H442.43V334.07L346.6 233.27V327.94V327.93Z"
                    fill="currentColor"
                  />
                  <path
                    d="M20.64 327.82H0L27.81 184.49L72.26 287.77L120.16 184.49L145.17 327.82H124.53L111.06 247.2L72.26 333.45L34.45 247.03L20.63 327.83H20.64V327.82Z"
                    fill="currentColor"
                  />
                  <path
                    d="M72.2798 333.45L58.7998 356.39L72.2798 379.33L85.7598 356.39L72.2798 333.45Z"
                    fill="currentColor"
                  />
                  <g fill="#EA0034">
                    <path d="M160.13 125.65C161.24 126.42 162.35 126.82 163.43 126.82C163.86 126.82 164.29 126.76 164.71 126.64C166.08 126.23 167.35 125.16 168.63 123.35C169.51 122.11 170 120.37 169.96 118.62C170.03 117.52 169.75 116.53 169.51 115.89C169.38 115.52 169.24 115.22 169.12 115.01L168.9 114.64L168.63 114.49C167.41 113.77 166.1 113.75 164.78 114.45C163.4 115.17 161.88 116.72 160.23 119.05C159.17 120.56 158.63 121.78 158.58 122.8C158.52 123.92 159.06 124.91 160.14 125.68V125.65H160.13Z" />
                    <path d="M276.57 128.62C277.68 129.39 278.79 129.79 279.87 129.79C280.3 129.79 280.73 129.73 281.15 129.61C282.52 129.2 283.79 128.13 285.07 126.32C285.95 125.07 286.44 123.34 286.41 121.59C286.47 120.49 286.2 119.5 285.96 118.86C285.83 118.5 285.69 118.2 285.59 118L285.38 117.63L285.1 117.46C283.89 116.75 282.56 116.74 281.25 117.42C279.87 118.14 278.34 119.69 276.7 122.02C275.64 123.53 275.1 124.75 275.05 125.77C274.99 126.89 275.53 127.88 276.61 128.65L276.59 128.62H276.57Z" />
                    <path d="M462.73 58.1404L462.69 58.0504C462.05 56.8104 461.48 55.9504 459.9 56.1204C458.24 56.2904 456.21 58.6904 455.77 60.4304C455.59 61.0004 455.54 61.5504 455.59 62.0804C455.69 63.0604 456.24 63.6004 456.65 63.9404C456.32 64.3804 455.68 64.9504 455.05 65.5004C454.68 65.8404 454.28 66.1804 453.97 66.4904C450.13 69.5104 445.22 71.2604 438.58 73.0904C437.05 73.3904 435.51 73.7104 433.98 74.0404L433.84 74.0704C429.79 74.9304 425.59 75.8204 421.13 76.2904C405.98 77.8904 390.56 78.7304 375.33 79.0704C375.78 79.0604 374.94 79.4704 389.52 59.9504C395.57 51.8504 411.04 33.5504 418.45 18.8104C418.59 18.5104 418.73 18.2004 418.87 17.9004C420.63 14.1004 422.39 8.77042 422.15 4.52042C422.11 3.81042 422.06 2.63042 421.39 1.88042L421.26 1.68042C420.5 0.480425 419.21 -0.089575 417.52 0.000425016C416.12 0.070425 414.58 0.740425 413.13 1.87042C409.75 4.52043 406.4 8.97043 404.03 12.4504C391.25 31.2604 385.8 41.1304 377.47 54.2804C374.58 58.8404 367.73 68.9304 364.76 73.3404C363.64 75.0204 362.26 77.7104 361.32 79.2104C349.79 79.4704 341.91 79.2404 328.28 80.6504C321.99 81.3104 301.77 84.8104 294.1 89.9804C284.78 96.1804 284.82 99.5604 284.83 101V101.15C284.83 101.91 285.13 102.63 285.69 103.16C286.3 103.72 287.15 104.02 287.91 103.93C288.75 103.84 289.53 103.07 291.73 100.18L292.08 99.7204C298.87 90.9904 311.75 85.0404 357.49 85.5104C334.55 122.46 293.39 171.89 268.65 173.83C265.17 174.09 264.13 173.39 263.44 172.01C262.33 169.81 263 164.83 272.52 153.7C274.39 151.59 275.02 150.58 275.02 149.73C275.02 147.29 272.48 146.43 270.31 146.43C269.56 146.43 268.56 146.52 268 146.93C265.3 148.66 260.75 153.52 259.52 155.3C259.04 155.96 258.55 156.68 258.08 157.45C247.47 162.84 242.36 166.51 232.23 173.78L231.77 174.12C230.2 171.64 228.57 169.34 227.01 167.09C222.48 160.64 218.56 155.07 218.56 149.27C218.56 145.97 219.73 142.81 222.13 139.61C226.32 134.08 230.5 131.47 232.63 130.4C233.66 129.82 234.32 129.68 234.68 129.64V129.77C234.68 131.55 232.13 135.53 230.27 138.45C228.28 141.54 227.16 143.36 227.02 144.6C226.83 146.21 227.21 147.58 228.17 148.66C229.86 150.56 232.66 150.76 233.78 150.76C235.89 150.76 238.38 149.45 239.74 147.54C239.92 147.14 244.17 137.52 244.17 128.79C244.17 127.95 244.17 127.23 244.03 126.55C243.24 120.28 237.98 118.7 229.59 122.36C222.55 125.88 217.24 130.02 213.81 134.66C210.82 138.68 209.38 142.69 209.38 146.92C209.38 151.68 211.2 156.54 215.11 162.2C203.57 166.91 170.01 180.8 145.63 179.02C141.98 178.75 139.4 177.83 138.56 176.48C134.36 169.78 147.3 155.48 152.85 149.37L154.08 148.02C155.54 146.37 156.22 144.98 156.22 143.65C156.22 140.53 152.37 139.39 149.77 139.39C148.8 139.39 147.9 139.57 147.19 139.93C143.77 142.19 137.21 149.56 135.47 151.93C132.56 155.89 128.95 161.86 128.39 167.8C127.98 172.08 128.76 175.21 130.98 178.28C133.73 182.06 138.81 184.55 144.92 185.09C146.39 185.22 147.98 185.28 149.63 185.28C173.82 185.28 206.41 171.66 217.76 165.75C219.11 167.48 220.53 169.17 221.8 170.68C223.98 173.28 226.04 175.75 227.53 178.02C217.69 185.49 210.1 192.66 204.48 199.08C197.47 207.05 193.49 213.88 191.98 218.7C191.34 220.97 191.01 222.97 191.01 224.81C191.01 227.44 191.47 230.15 192.74 232.49C193.05 233.07 193.41 233.61 193.83 234.11C194.82 235.35 196.1 236.35 197.6 236.98C198.87 237.51 200.28 237.77 201.64 237.96C204.46 238.36 207.37 238.23 210.19 237.83C212.31 237.53 214.41 237 216.38 236.18C221.64 233.99 225.46 229.47 228.63 224.9C231.57 220.68 233.75 216.42 235.8 211.72C235.91 211.48 236.01 211.23 236.11 210.99H235.66C232.22 210.99 228.93 211.27 225.79 211.83C225.53 212.3 225.29 212.78 225.02 213.23C224.08 214.78 222.94 216.16 221.74 217.51C219.22 220.35 216.4 222.94 213.24 225.04C210.3 227 206.63 228.05 203.08 227.6C201.94 227.46 200.42 227.15 199.62 226.22C199.58 226.17 199.53 226.11 199.49 226.05C199.16 225.57 199.04 224.98 198.99 224.4C198.7 221.43 199.93 218.83 201.29 216.28C202.65 213.73 204.24 211.29 205.93 208.94C208.21 205.78 210.72 202.77 213.35 199.9C215.18 197.91 217.08 195.97 219.04 194.1C220.62 192.58 222.24 191.1 223.9 189.66C224.9 188.79 225.91 187.93 226.93 187.08C227.46 186.64 227.99 186.21 228.52 185.78C228.7 185.64 230.17 184.64 230.17 184.47C230.71 187.12 230.93 189.84 230.81 192.54C232.35 192.45 233.91 192.41 235.49 192.41C236.43 192.41 237.36 192.43 238.28 192.46C238.11 188.55 237.04 184.5 235.18 180.78C244.27 174.17 246.9 172.92 254.38 169.38C254.56 171.37 255.08 172.49 255.97 174.28C257.83 177.98 262.04 180.07 267.06 179.71C295.37 177.66 323.89 142.53 342.77 119.27C344.85 116.72 346.84 114.24 348.71 112C344.56 119.74 331 157.79 343.15 181.58C344.32 183.87 349.69 194.1 353.44 196.01C355.88 197.25 361.5 193.95 360.94 192.46C360.18 190.4 357.79 191.51 353.13 186.25C339.9 169.9 341.48 148.62 353.92 116.19C359.43 101.84 368.97 85.3804 369.81 85.2104C373.99 84.3604 378.39 84.4304 382.64 84.3604C387.6 84.2704 392.52 83.9704 397.47 83.5704C410.73 82.5004 424.46 81.5404 437.39 78.3104C443.71 76.8304 451 75.0904 456.46 71.3804C459.55 69.3004 463.15 66.5604 463.47 63.6404C463.74 61.1304 463.43 59.7704 462.73 58.1404ZM412.74 14.3404C409.11 20.2604 403.51 28.5704 399.22 35.2104C403.17 28.7404 408.77 20.4804 412.74 14.3404Z" />
                  </g>
                </svg>
              </td>
            </tr>
          </table>
        </div>
      </body>
    </html>
  );
};

export { SocialImageLayout };
